import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useTranslate } from "@tolgee/react";
import { useMediaDeviceSelect } from "@livekit/components-react";
import { MessageCircle, BarChart2, LogOut } from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

import { colors } from "../Assets/styles/colors";
import { useAuthService } from "../Context/AuthContext";
import { useCallsService } from "../Context/CallsContext";
import { useWebSocketDataService } from "../Context/WebsocketContext";
import { useConferenceAttendeeSession } from "../hooks/useConferenceAttendeeSession";
import { normalizeConferenceLanguageKey } from "../Helpers/callHelper/conferenceLanguageKey";
import { setupSignedHls } from "../Helpers/callHelper/setupSignedHls";
import LanguageListSelector from "../Components/Auth Components/LanguageListSelector";
import { ReactComponent as Unfold } from "../Assets/icons/Unfold_More.svg";
import { ReactComponent as EmotiiVoice } from "../Assets/iconsv2/controlButtons/emotiiVoice.svg";
import VolumeStepper from "../Components/GenericComponents/VolumeStepper";
import CallChatPanel from "../Components/CallChatComponents/CallChatPanel";
import PollPanel from "../Components/Conferences/PollPanel";
import { useNotify } from "../Helpers/Notifications";

const DEFAULT_CALL_LANGUAGE = "en-US";

const FALLBACK_CAPTION_TIMEOUT_MS = 15000;
const TTS_SPEAKING_RMS_THRESHOLD = 0.02;
const TTS_SILENCE_HOLD_MS = 400;
const CAPTION_TICK_INTERVAL_MS = 200;

const ConferenceInPersonTranslationPage = () => {
  const { t } = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const { notifyInfo } = useNotify();
  const {
    user,
    updateUser,
    handleLoginUser,
    handleLogoutUser,
    callTranslationsLanguages,
    getCallTranslationsLanguages,
  } = useAuthService();
  const { selectedAudioOutput, setSelectedAudioOutput } = useCallsService();
  const { add_observer, remove_observer } = useWebSocketDataService();

  const {
    conferenceId,
    callId,
    callUuid,
    conferenceTitle,
    allowChat = true,
    allowPolls = true,
  } = location.state || {};

  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [volume, setVolume] = useState(100);
  const [activePanel, setActivePanel] = useState(null);
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const tokenRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);
  const activeCallRef = useRef(null);
  const defaultLanguageAppliedRef = useRef(false);
  const callChatConversationIdRef = useRef(null);

  const captionSeenAtRef = useRef(new Map());
  const captionRevealAtRef = useRef(new Map());
  const ttsOnsetQueueRef = useRef([]);
  const [captionTick, setCaptionTick] = useState(0);

  const audioCtxRef = useRef(null);
  const [needsAudioGesture, setNeedsAudioGesture] = useState(false);

  useEffect(() => {
    if (!conferenceId || !callUuid) {
      navigate("/login", { replace: true });
    }
  }, [conferenceId, callUuid, navigate]);

  useEffect(() => {
    activeCallRef.current = {
      details: { id: callId, uuid: callUuid, conference_id: conferenceId },
    };
  }, [callId, callUuid, conferenceId]);

  useEffect(() => {
    if (!callId) return;

    const observer = {
      channelName: "ConversationsChannel",
      update: (rawData) => {
        const wsData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        const matchesCall =
          (wsData.call_id && wsData.call_id === callId) ||
          (wsData.conference_call_id && wsData.conference_call_id === callId);
        if (matchesCall && wsData.id) {
          callChatConversationIdRef.current = wsData.id;
          setUnreadChatCount(wsData.unread_messages_ids?.length || 0);
          return;
        }
        if (wsData.id === callChatConversationIdRef.current) {
          setUnreadChatCount(wsData.unread_messages_ids?.length || 0);
        }
      },
    };

    add_observer(observer);
    return () => remove_observer(observer);
  }, [callId, add_observer, remove_observer]);

  useEffect(() => {
    if (!callTranslationsLanguages || callTranslationsLanguages.length === 0) {
      getCallTranslationsLanguages?.(() => {}, () => {});
    }
  }, [callTranslationsLanguages, getCallTranslationsLanguages]);

  useEffect(() => {
    if (defaultLanguageAppliedRef.current) return;
    if (!user || user.call_language) return;

    defaultLanguageAppliedRef.current = true;
    const english =
      callTranslationsLanguages?.find((l) => /^en/i.test(l.locale)) ||
      callTranslationsLanguages?.[0];
    const localeToApply = english?.locale || DEFAULT_CALL_LANGUAGE;

    updateUser(
      { call_language: localeToApply },
      (response) => {
        const updatedUser = response?.data?.user;
        if (updatedUser) handleLoginUser(updatedUser);
      },
      () => {}
    );
  }, [user, callTranslationsLanguages, updateUser, handleLoginUser]);

  const languageKey = useMemo(
    () => normalizeConferenceLanguageKey(user?.call_language || DEFAULT_CALL_LANGUAGE),
    [user?.call_language]
  );

  const { callAccess, speechRecognitionMessages, requestLanguageSwitch, callEnded } =
    useConferenceAttendeeSession({
      conferenceId,
      callUuid,
      languageKey,
      joinMode: "in_person",
    });

  const signOutOfConferenceSession = () => {
    handleLogoutUser();
    signOut(auth).catch(() => {});
    sessionStorage.removeItem("conference_scoped_auth");
  };

  useEffect(() => {
    if (!callEnded) return;
    notifyInfo(t("conferences.conference_has_ended"));
    signOutOfConferenceSession();
    navigate("/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callEnded, notifyInfo, t, navigate]);

  const ttsStream =
    callAccess?.streams?.find((s) => s.language_key === languageKey) ??
    callAccess?.streams?.[0];
  const ttsPlaybackUrl = ttsStream?.playback_url ?? null;
  const ttsPlaybackId = ttsStream?.playback_id ?? null;
  const isAudioActive = !!ttsPlaybackUrl;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !ttsPlaybackUrl) return;

    captionSeenAtRef.current = new Map();
    captionRevealAtRef.current = new Map();
    ttsOnsetQueueRef.current = [];

    return setupSignedHls({
      media: audio,
      hlsRefSlot: hlsRef,
      tokenRef,
      conferenceId,
      playbackId: ttsPlaybackId,
      initialUrl: ttsPlaybackUrl,
      onManifestParsed: () => {
        audio.play().catch(() => {});
      },
    });
  }, [ttsPlaybackId, ttsPlaybackUrl, conferenceId]);

  const displayedMessages = useMemo(() => {
    const list = speechRecognitionMessages || [];
    if (!ttsPlaybackUrl) return list;

    const now = Date.now();
    const seen = captionSeenAtRef.current;
    const revealAt = captionRevealAtRef.current;
    const pending = ttsOnsetQueueRef.current;

    for (let i = list.length - 1; i >= 0; i--) {
      const message = list[i];
      const id = message?.id;
      if (id == null || seen.has(id)) continue;
      seen.set(id, now);
      pending.push({ id, deadline: now + FALLBACK_CAPTION_TIMEOUT_MS });
    }

    for (let i = pending.length - 1; i >= 0; i--) {
      const entry = pending[i];
      if (now >= entry.deadline) {
        if (!revealAt.has(entry.id)) revealAt.set(entry.id, now);
        pending.splice(i, 1);
      }
    }

    return list.filter((message) => {
      if (message?.id == null) return true;
      return revealAt.has(message.id) && now >= revealAt.get(message.id);
    });
  }, [speechRecognitionMessages, captionTick, ttsPlaybackUrl]);

  useEffect(() => {
    if (!ttsPlaybackUrl) return;
    const id = setInterval(() => {
      setCaptionTick((tick) => (tick + 1) % 1000000);
    }, CAPTION_TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [ttsPlaybackUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let ctx, source, analyser, raf;
    let cancelled = false;
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      ctx = new Ctor();
      audioCtxRef.current = ctx;
      source = ctx.createMediaElementSource(audio);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyser.connect(ctx.destination);
    } catch (e) {
      return;
    }

    const buffer = new Uint8Array(analyser.fftSize);
    let speaking = false;
    let silenceSince = 0;
    const tick = () => {
      if (cancelled) return;
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        const v = (buffer[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buffer.length);
      const now = performance.now();
      const wasSpeaking = speaking;
      if (rms > TTS_SPEAKING_RMS_THRESHOLD) {
        speaking = true;
        silenceSince = 0;
      } else if (speaking) {
        if (!silenceSince) silenceSince = now;
        if (now - silenceSince > TTS_SILENCE_HOLD_MS) speaking = false;
      }
      if (!wasSpeaking && speaking) {
        const next = ttsOnsetQueueRef.current.shift();
        if (next != null && !captionRevealAtRef.current.has(next.id)) {
          captionRevealAtRef.current.set(next.id, Date.now());
        }
      }
      raf = requestAnimationFrame(tick);
    };

    const GESTURES = ["click", "pointerdown", "touchstart", "keydown"];
    const resume = () => {
      ctx.resume().then(() => {
        if (!cancelled) setNeedsAudioGesture(ctx.state !== "running");
      }).catch(() => {});
    };
    resume();
    if (ctx.state !== "running") setNeedsAudioGesture(true);
    GESTURES.forEach((evt) => window.addEventListener(evt, resume, { passive: true }));
    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      GESTURES.forEach((evt) => window.removeEventListener(evt, resume));
      try { source.disconnect(); analyser.disconnect(); } catch (e) {}
      ctx.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, []);

  const handleEnableAudio = () => {
    const ctx = audioCtxRef.current;
    if (ctx) {
      ctx.resume().then(() => setNeedsAudioGesture(ctx.state !== "running")).catch(() => {});
    }
    audioRef.current?.play().catch(() => {});
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume / 100;
    audio.muted = volume === 0;
  }, [volume]);

  const audioOutputDevice = useMediaDeviceSelect({ kind: "audiooutput", requestPermissions: showSettings });

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && selectedAudioOutput && audio.setSinkId) {
      audio.setSinkId(selectedAudioOutput).catch(() => {});
    }
    const ctx = audioCtxRef.current;
    if (ctx && selectedAudioOutput && typeof ctx.setSinkId === "function") {
      ctx.setSinkId(selectedAudioOutput).catch(() => {});
    }
  }, [selectedAudioOutput]);

  const latestMessage = displayedMessages[0];
  const latestMessageSignature = latestMessage
    ? `${latestMessage.id}-${latestMessage.call_message_translations?.[0]?.translation || ""}`
    : "";

  useEffect(() => {
    const container = messageListRef.current;
    if (!container || !messagesEndRef.current) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom > 150) return;

    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [latestMessageSignature]);

  const handleLanguageSelected = (newLocale) => {
    setIsLanguageSelectorOpen(false);
    if (!newLocale || newLocale === user?.call_language) return;

    updateUser(
      { call_language: newLocale },
      (response) => {
        const updatedUser = response?.data?.user;
        if (updatedUser) handleLoginUser(updatedUser);
        requestLanguageSwitch(normalizeConferenceLanguageKey(newLocale));
      },
      () => {}
    );
  };

  const handleOutputDeviceSelected = (deviceId) => {
    if (!deviceId) return;
    audioOutputDevice.setActiveMediaDevice(deviceId);
    setSelectedAudioOutput(deviceId);
  };

  const handleLeave = () => {
    signOutOfConferenceSession();
    navigate("/login", { replace: true });
  };

  const currentLanguageName =
    callTranslationsLanguages?.find((l) => l.locale === user?.call_language)?.name ||
    "English";

  const outputDevices = (audioOutputDevice.devices || []).filter(
    (d) => d.deviceId !== "default" && d.deviceId !== "communications" && d.label
  );

  return (
    <PageWrapper>
      {needsAudioGesture && (
        <AudioGestureOverlay onClick={handleEnableAudio}>
          <AudioGesturePill type="button">
            <EmotiiVoice style={{ width: "20px", height: "20px" }} />
            {t("conferences.tap_for_sound")}
          </AudioGesturePill>
        </AudioGestureOverlay>
      )}

      <Header>
        <HeaderLeft>
          <BrandLogo src="/emotii-ai-white.svg" alt="emotii" />
          <HeaderTitle>{conferenceTitle || t("conferences.title")}</HeaderTitle>
        </HeaderLeft>
        <HeaderRightActions>
          <IconButton title={t("call_modal.leave")} onClick={handleLeave}>
            <LogOut size={18} />
          </IconButton>
        </HeaderRightActions>
      </Header>

      <ChatArea>
        <MessageList ref={messageListRef}>
          {[...displayedMessages].reverse().map((msg, index) => {
            const translatedText = msg.call_message_translations?.[0]?.translation;
            const isLastMessage = index === displayedMessages.length - 1;

            return (
              <MessageRow key={msg.id || index}>
                <Bubble>
                  <OriginalText>{msg.original_message}</OriginalText>
                  {translatedText ? (
                    <TranslatedText>{translatedText}</TranslatedText>
                  ) : isLastMessage ? (
                    <TypingIndicator>
                      <span /><span /><span />
                    </TypingIndicator>
                  ) : null}
                </Bubble>
              </MessageRow>
            );
          })}
          <div ref={messagesEndRef} />
        </MessageList>
      </ChatArea>

      <ControlBar>
        <WavesContainer $isListening={isAudioActive}>
          <SvgWave $duration="15s" $isListening={isAudioActive} viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ filter: "blur(3px)" }}>
            <path d="M0 60 Q150 20 300 60 T600 60 T900 60 T1200 60 L1200 120 L0 120 Z" fill="rgba(255, 255, 255, 0.03)" />
          </SvgWave>
          <SvgWave $duration="10s" $isListening={isAudioActive} viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ filter: "blur(1px)" }}>
            <path d="M0 80 Q150 40 300 80 T600 80 T900 80 T1200 80 L1200 120 L0 120 Z" fill="rgba(255, 255, 255, 0.04)" />
          </SvgWave>
          <SvgWave $duration="7s" $isListening={isAudioActive} viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0 90 Q150 75 300 90 T600 90 T900 90 T1200 90" fill="none" stroke="rgba(255, 255, 255, 0.25)" strokeWidth="0.5" />
          </SvgWave>
          <SvgWave $duration="9s" $isListening={isAudioActive} viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0 90 Q150 105 300 90 T600 90 T900 90 T1200 90" fill="none" stroke={colors.purple || "#8e44ad"} opacity="0.8" strokeWidth="0.5" />
          </SvgWave>
        </WavesContainer>

        <ControlsRow>
          <TranslateGroup>
            <TranslateLabel>{t("live_feed.translate_to")}</TranslateLabel>
            <LanguageTrigger onClick={() => setIsLanguageSelectorOpen(true)}>
              <LanguageName>{currentLanguageName}</LanguageName>
              <UnfoldIconWrapper><Unfold /></UnfoldIconWrapper>
            </LanguageTrigger>
          </TranslateGroup>

          <ActionsGroup>
            {allowPolls && (
              <IconButton
                title={t("call_modal.polls")}
                $isActive={activePanel === "polls"}
                onClick={() => setActivePanel((p) => (p === "polls" ? null : "polls"))}
              >
                <BarChart2 size={18} />
              </IconButton>
            )}
            {allowChat && (
              <IconButton
                title={t("call_modal.chat")}
                $isActive={activePanel === "chat"}
                onClick={() => {
                  setActivePanel((p) => (p === "chat" ? null : "chat"));
                  setUnreadChatCount(0);
                }}
              >
                <MessageCircle size={18} />
                {unreadChatCount > 0 && activePanel !== "chat" && <UnreadBadge />}
              </IconButton>
            )}
            <VoicePillButton $isActive={showSettings} onClick={() => setShowSettings(!showSettings)}>
              <EmotiiVoice style={{ width: "18px", height: "18px" }} />
              {t("live_feed.voice_label")}
            </VoicePillButton>
          </ActionsGroup>
        </ControlsRow>
      </ControlBar>

      {showSettings && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShowSettings(false)} />
          <SettingsCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <h3 style={{ color: "white", fontSize: "18px", margin: 0, fontWeight: "600" }}>
                  {t("live_feed.voice_settings_title")}
                </h3>
              </div>
              <IconButton
                onClick={() => setShowSettings(false)}
                style={{ padding: "4px", background: "rgba(255,255,255,0.05)", marginTop: "-2px" }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </IconButton>
            </div>

            <SettingGroup>
              <SettingLabel>{t("live_feed.volume")}</SettingLabel>
              <VolumeStepper value={volume} onChange={(e) => setVolume(parseInt(e.target.value, 10))} />
            </SettingGroup>

            {outputDevices.length > 0 && (
              <SettingGroup>
                <SettingLabel>{t("callOptionModal.audio_output")}</SettingLabel>
                <OutputSelect
                  value={audioOutputDevice.activeDeviceId || ""}
                  onChange={(e) => handleOutputDeviceSelected(e.target.value)}
                >
                  {outputDevices.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || device.deviceId}
                    </option>
                  ))}
                </OutputSelect>
              </SettingGroup>
            )}
          </SettingsCard>
        </>
      )}

      {isLanguageSelectorOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 99999 }}>
          <LanguageListSelector
            isLanguageSelectorOpen={isLanguageSelectorOpen}
            openLanguageList={() => setIsLanguageSelectorOpen(false)}
            valuesList={callTranslationsLanguages || []}
            setValue={handleLanguageSelected}
            selectedValue={user?.call_language}
            callLanguage
          />
        </div>
      )}

      {activePanel === "chat" && callId && (
        <SidePanel>
          <CallChatPanel
            callInfo={{ id: callId, conference_id: conferenceId, conference_role: "attendee" }}
            activeCallRef={activeCallRef}
            existingConversation={null}
            onConversationCreated={() => {}}
            onMessagesRead={() => setUnreadChatCount(0)}
            onClose={() => setActivePanel(null)}
          />
        </SidePanel>
      )}

      {activePanel === "polls" && conferenceId && (
        <SidePanel>
          <PollPanel
            conferenceId={conferenceId}
            isOrganizer={false}
            isPresenter={false}
            onClose={() => setActivePanel(null)}
          />
        </SidePanel>
      )}

      <audio ref={audioRef} autoPlay playsInline style={{ display: "none" }} />
    </PageWrapper>
  );
};

export default ConferenceInPersonTranslationPage;

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  width: 100%;
  background-color: #121218;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 17px 24px;
  padding-top: calc(17px + env(safe-area-inset-top, 0px));
  background: rgba(28, 28, 35, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  z-index: 10;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const BrandLogo = styled.img`
  height: 20px;
  width: auto;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HeaderRightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  position: relative;
  background: ${(props) => (props.$isActive ? "rgba(61, 58, 99, 0.8)" : "transparent")};
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const UnreadBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 12px;
  height: 12px;
  background: #ff3b30;
  border-radius: 50%;
  border: 2px solid #121218;
  pointer-events: none;
`;

const VoicePillButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${(props) => (props.$isActive ? "rgb(61, 58, 99)" : "#4b4b52")};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.$isActive ? "rgb(76, 73, 114)" : "#5a5a63")};
  }
`;

const AudioGestureOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(10, 10, 14, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  cursor: pointer;
`;

const AudioGesturePill = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: rgb(61, 58, 99);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 28px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6);

  &:hover {
    background-color: rgb(76, 73, 114);
  }
`;

const ChatArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 10px 20px;
  gap: 10px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MessageRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  animation: ${slideUp} 0.15s ease-out forwards;
`;

const Bubble = styled.div`
  width: 100%;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  overflow-wrap: break-word;
  word-break: break-word;
`;

const TranslatedText = styled.span`
  font-size: 16px;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const OriginalText = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 4px 2px;
  height: 20px;

  span {
    width: 6px;
    height: 6px;
    background-color: white;
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out both;
  }
  span:nth-child(1) { animation-delay: -0.32s; }
  span:nth-child(2) { animation-delay: -0.16s; }
  span:nth-child(3) { animation-delay: 0s; }
`;

const slideInMenu = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const SettingsCard = styled.div`
  position: fixed;
  bottom: 100px;
  left: 16px;
  right: 16px;
  width: auto;
  max-width: 280px;
  margin: 0 auto;
  padding: 16px;
  background: rgba(28, 28, 35, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  z-index: 250;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.2);
  animation: ${slideInMenu} 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  transform-origin: bottom center;
`;

const SettingGroup = styled.div`
  margin-bottom: 16px;
  &:last-child { margin-bottom: 0; }
`;

const SettingLabel = styled.div`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  letter-spacing: 0.3px;
`;

const OutputSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 13px;
`;

const slideWave = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const WavesContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  opacity: ${(props) => (props.$isListening ? 1 : 0.3)};
  transition: opacity 0.5s ease-in-out;
`;

const SvgWave = styled.svg`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100%;
  animation: ${slideWave} ${(props) => props.$duration} linear infinite;
  animation-play-state: ${(props) => (props.$isListening ? "running" : "paused")};
  transform-origin: bottom center;
`;

const ControlBar = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 10px 24px calc(30px + env(safe-area-inset-bottom, 0px)) 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: linear-gradient(180deg, rgba(30,30,35,0) 0%, rgba(18,18,18,1) 80%);
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  background: rgba(28, 28, 35, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 40px;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: 6px 6px 6px 20px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.6);
  z-index: 10;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TranslateGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TranslateLabel = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  font-weight: 500;

  @media (max-width: 480px) {
    display: none;
  }
`;

const LanguageTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const LanguageName = styled.p`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: #ffffff !important;
  letter-spacing: 0.2px;
`;

const UnfoldIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 14px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SidePanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  height: 100dvh;
  width: min(380px, 100vw);
  z-index: 200;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.4);
`;
